import type { PresenceUser, ServerEvent } from "@talkers/shared";
import { createRoot, createSignal, onCleanup } from "solid-js";

type Listener = (event: ServerEvent) => void;

const WS_ORIGIN = import.meta.env.DEV ? "" : (import.meta.env.VITE_API_ORIGIN ?? "");

function wsUrl(): string {
  const base = WS_ORIGIN === "" ? window.location.origin : WS_ORIGIN;
  return `${base.replace(/^http/, "ws")}/ws`;
}

export const { online, connected, onEvent, onReconnect, subscribeThread, reconnect } = createRoot(
  () => {
    const [online, setOnline] = createSignal<PresenceUser[]>([]);
    const [connected, setConnected] = createSignal(false);
    const listeners = new Set<Listener>();
    const threads = new Set<number>();

    let socket: WebSocket | null = null;
    let retry = 0;
    /** 一度でも切れたか。切れている間の更新は届かないので、繋ぎ直したら取り直させる。 */
    let wasDisconnected = false;
    const reconnected = new Set<() => void>();

    const send = (message: unknown) => {
      if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(message));
    };

    const connect = () => {
      socket = new WebSocket(wsUrl());

      socket.addEventListener("open", () => {
        retry = 0;
        setConnected(true);
        for (const threadId of threads) send({ type: "subscribe.thread", threadId });
        if (wasDisconnected) {
          wasDisconnected = false;
          for (const listener of reconnected) listener();
        }
      });

      socket.addEventListener("message", (raw) => {
        const event: ServerEvent = JSON.parse(String(raw.data));
        if (event.type === "hello" || event.type === "presence") setOnline(event.online);
        for (const listener of listeners) listener(event);
      });

      socket.addEventListener("close", () => {
        setConnected(false);
        wasDisconnected = true;
        socket = null;
        retry += 1;
        window.setTimeout(connect, Math.min(1000 * retry, 10_000));
      });
    };

    connect();

    return {
      online,
      connected,
      reconnect: () => {
        socket?.close();
      },
      /** 繋ぎ直したときに呼ばれる。切れていた間の更新を取り直すために使う。 */
      onReconnect(listener: () => void) {
        reconnected.add(listener);
      },
      onEvent(listener: Listener) {
        listeners.add(listener);
        onCleanup(() => listeners.delete(listener));
      },
      subscribeThread(threadId: number) {
        threads.add(threadId);
        send({ type: "subscribe.thread", threadId });
        onCleanup(() => {
          threads.delete(threadId);
          send({ type: "unsubscribe.thread", threadId });
        });
      },
    };
  },
);
